import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../api/client";
import {
  useBrowse,
  useCatalog,
  useDeleteFlight,
  useFlightRoots,
  useFlights,
  useRegisterFlights,
  useSubmitRun,
  useUpdateFlight,
  useUploadFlight,
} from "../../api/hooks";
import type { FlightQuality, FlightRecord } from "../../api/types";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText, fmtDate, fmtDuration } from "../../components/ui";
import "./flights.css";

export function FlightsPage() {
  const flights = useFlights();
  const { projectId, selectedFlights, toggleFlight, setSelectedFlights, activeFlight, setActiveFlight, toast, setCurrentRun, setDock } =
    useSession();
  const [browsing, setBrowsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const [tailFilter, setTailFilter] = useState("");
  const [trace, setTrace] = useState<"off" | "bool" | "all">("bool");
  const [onlyTriggers, setOnlyTriggers] = useState<string[]>([]);
  const [batchTags, setBatchTags] = useState("");
  const del = useDeleteFlight();
  const update = useUpdateFlight();
  const submit = useSubmitRun();
  const catalog = useCatalog(projectId);
  const navigate = useNavigate();

  const tails = useMemo(() => [...new Set((flights.data ?? []).map((f) => f.tail ?? "unknown"))].sort(), [flights.data]);

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (flights.data ?? []).filter(
      (r) =>
        (!tailFilter || (r.tail ?? "unknown") === tailFilter) &&
        (!f || [r.tail, r.id, r.source_path, r.date, ...(r.tags ?? [])].some((x) => (x ?? "").toLowerCase().includes(f))),
    );
  }, [flights.data, filter, tailFilter]);

  const allSelected = rows.length > 0 && rows.every((r) => selectedFlights.includes(r.id));
  const active = flights.data?.find((f) => f.id === activeFlight) ?? null;
  const selectedRecords = useMemo(() => (flights.data ?? []).filter((f) => selectedFlights.includes(f.id)), [flights.data, selectedFlights]);

  const runSelected = () => {
    if (!projectId || !selectedFlights.length) return;
    const n = selectedFlights.length;
    submit.mutate(
      {
        project: projectId,
        flights: selectedFlights,
        trace,
        only_triggers: onlyTriggers.length ? onlyTriggers : undefined,
        label: `batch ${n} flight${n > 1 ? "s" : ""}${onlyTriggers.length ? ` · ${onlyTriggers.join(",")}` : ""}`,
      },
      {
        onSuccess: (r) => {
          setCurrentRun(r.id);
          setDock(true, "runlog");
          toast(`Run ${r.id} started on ${n} flight(s)`, "success");
          navigate(n > 1 ? "/runs" : "/analysis");
        },
        onError: (e) => toast(errText(e), "error"),
      },
    );
  };

  const tagSelected = async (mode: "add" | "remove") => {
    const tags = batchTags.split(",").map((t) => t.trim()).filter(Boolean);
    if (!tags.length) return;
    for (const rec of selectedRecords) {
      const next = mode === "add" ? [...new Set([...rec.tags, ...tags])] : rec.tags.filter((t) => !tags.includes(t));
      if (next.length !== rec.tags.length || mode === "add") await update.mutateAsync({ id: rec.id, tags: next }).catch((e) => toast(errText(e), "error"));
    }
    toast(`${mode === "add" ? "Tagged" : "Untagged"} ${selectedRecords.length} flight(s)`, "success");
  };

  const removeSelected = async () => {
    if (!confirm(`Remove ${selectedFlights.length} flight(s) from the workspace? (source files are not touched)`)) return;
    for (const id of selectedFlights) await del.mutateAsync(id).catch((e) => toast(errText(e), "error"));
    setSelectedFlights([]);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>Flights</h1>
        <span className="sub">{flights.data?.length ?? 0} registered</span>
        <span className="spacer" />
        <select className="inp sm" value={tailFilter} onChange={(e) => setTailFilter(e.target.value)} title="Filter by tail">
          <option value="">all tails</option>
          {tails.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input className="inp sm" placeholder="filter tail / path / tag" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className="btn" onClick={() => setBrowsing(true)}>
          ＋ From server folder
        </button>
        <button className="btn" onClick={() => setUploading(true)}>
          ⤒ Upload .tsc
        </button>
        <button className="btn primary" disabled={!projectId || !selectedFlights.length || submit.isPending} onClick={runSelected}>
          ▶ Run project on {selectedFlights.length || "selected"}
        </button>
      </div>
      {selectedFlights.length ? (
        <div className="batch-bar row wrap">
          <strong>{selectedFlights.length} selected</strong>
          <span className="dim small">
            {[...new Set(selectedRecords.map((r) => r.tail ?? "?"))].join(", ")}
          </span>
          <span className="sep" />
          <label className="row small" style={{ gap: 4 }}>
            trace
            <select className="inp sm" value={trace} onChange={(e) => setTrace(e.target.value as "off" | "bool" | "all")} title="How much evaluation trace to persist for Analysis">
              <option value="off">off</option>
              <option value="bool">booleans</option>
              <option value="all">all</option>
            </select>
          </label>
          <label className="row small" style={{ gap: 4 }} title="Restrict the batch to some triggers">
            triggers
            <select
              className="inp sm"
              multiple
              size={1}
              value={onlyTriggers}
              onChange={(e) => setOnlyTriggers([...e.target.selectedOptions].map((o) => o.value))}
              style={{ minWidth: 120, height: 26 }}
            >
              {(catalog.data?.triggers ?? []).map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
            {onlyTriggers.length ? (
              <button className="btn sm ghost" onClick={() => setOnlyTriggers([])} title="all triggers">
                ✕ {onlyTriggers.length}
              </button>
            ) : (
              <span className="faint">all</span>
            )}
          </label>
          <span className="sep" />
          <input className="inp sm" placeholder="tag, tag2" value={batchTags} onChange={(e) => setBatchTags(e.target.value)} style={{ width: 120 }} />
          <button className="btn sm" disabled={!batchTags.trim() || update.isPending} onClick={() => void tagSelected("add")}>
            Tag
          </button>
          <button className="btn sm" disabled={!batchTags.trim() || update.isPending} onClick={() => void tagSelected("remove")}>
            Untag
          </button>
          <span className="spacer" />
          <button className="btn sm" onClick={() => setSelectedFlights(rows.map((r) => r.id))} disabled={allSelected}>
            Select all {rows.length} shown
          </button>
          <button className="btn sm" onClick={() => setSelectedFlights([])}>
            Clear
          </button>
          <button className="btn sm danger" disabled={del.isPending} onClick={() => void removeSelected()}>
            Remove {selectedFlights.length}
          </button>
        </div>
      ) : null}
      <div className="page-body">
        <Split id="flights" sizes={["1fr", 380]} minSizes={[360, 240]}>
          <div className="pane">
            {flights.isLoading ? (
              <div className="pane-body">
                <Spinner label="loading flights" />
              </div>
            ) : rows.length ? (
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => setSelectedFlights(allSelected ? [] : rows.map((r) => r.id))}
                      />
                    </th>
                    <th>Tail</th>
                    <th>Date (UTC)</th>
                    <th>Source</th>
                    <th className="num">Files</th>
                    <th>Quality</th>
                    <th>Tags</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`clickable${r.id === activeFlight ? " sel" : ""}`}
                      onClick={() => setActiveFlight(r.id)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedFlights.includes(r.id)} onChange={() => toggleFlight(r.id)} />
                      </td>
                      <td className="mono">{r.tail ?? <span className="faint">unknown</span>}</td>
                      <td className="mono">{fmtDate(r.date)}</td>
                      <td className="dim" title={r.source_path ?? ""}>
                        {r.uploaded ? <span className="badge info">upload</span> : null} {shortPath(r.source_path)}
                      </td>
                      <td className="num">{r.files.length}</td>
                      <td>
                        <QualityBadge q={r.quality} />
                      </td>
                      <td>
                        {r.tags.map((t) => (
                          <span key={t} className="badge">
                            {t}
                          </span>
                        ))}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn ghost sm"
                          title="remove"
                          onClick={() => {
                            if (confirm(`Remove flight ${r.tail ?? r.id}?`)) del.mutate(r.id);
                          }}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <Empty title="No flights yet">
                Register flights from a server-mounted folder or upload the <code>.tsc</code> files of a flight. Tail and date are
                inferred from the folder layout (<code>TAIL/&lt;hex time&gt;/av573*.tsc</code>) and can be edited.
              </Empty>
            )}
          </div>
          <div className="pane">
            <div className="pane-head">Flight</div>
            <div className="pane-body">
              {active ? <FlightCard flight={active} projectId={projectId} /> : <span className="dim">Select a flight to see its details.</span>}
            </div>
          </div>
        </Split>
      </div>
      {browsing ? <BrowseDialog onClose={() => setBrowsing(false)} /> : null}
      {uploading ? <UploadDialog onClose={() => setUploading(false)} /> : null}
    </div>
  );
}

function shortPath(p?: string | null): string {
  if (!p) return "";
  const parts = p.replace(/\\/g, "/").split("/");
  return parts.slice(-3).join("/");
}

function QualityBadge({ q }: { q?: FlightQuality | null }) {
  if (!q) return <span className="faint small">not decoded</span>;
  const pct = q.valid_pct ?? 100;
  const cls = pct >= 99 ? "ok" : pct >= 90 ? "warn" : "err";
  return (
    <span className={`badge ${cls}`} title={`${q.valid_frames}/${q.frames} frames, ${q.sync_segments} sync segment(s)`}>
      {pct.toFixed(1)}% · {fmtDuration(q.total_seconds)}
    </span>
  );
}

function FlightCard({ flight, projectId }: { flight: FlightRecord; projectId: string | null }) {
  const update = useUpdateFlight();
  const toast = useSession((s) => s.toast);
  const [tail, setTail] = useState(flight.tail ?? "");
  const [date, setDate] = useState(flight.date ?? "");
  const [tags, setTags] = useState(flight.tags.join(", "));
  const quality = useQuery({
    queryKey: ["quality", flight.id, projectId],
    queryFn: () => api.get<FlightQuality>(`/flights/${flight.id}/quality?project=${projectId}`),
    enabled: !!projectId,
    retry: false,
    staleTime: Infinity,
  });
  const dirty = tail !== (flight.tail ?? "") || date !== (flight.date ?? "") || tags !== flight.tags.join(", ");
  const q = quality.data ?? flight.quality;
  return (
    <div className="col">
      <div className="card">
        <h4>Identity</h4>
        <div className="col">
          <label className="field">
            <span>Tail</span>
            <input className="inp sm" value={tail} onChange={(e) => setTail(e.target.value)} />
          </label>
          <label className="field">
            <span>Start time (UTC, ISO)</span>
            <input className="inp sm" value={date} onChange={(e) => setDate(e.target.value)} placeholder="2025-09-02T14:27:52" />
          </label>
          <label className="field">
            <span>Tags (comma separated)</span>
            <input className="inp sm" value={tags} onChange={(e) => setTags(e.target.value)} />
          </label>
          <div className="row">
            <button
              className="btn sm primary"
              disabled={!dirty || update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    id: flight.id,
                    tail,
                    date,
                    tags: tags
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  },
                  { onSuccess: () => toast("Flight updated", "success"), onError: (e) => toast(errText(e), "error") },
                )
              }
            >
              Save
            </button>
            <span className="faint small mono">{flight.id}</span>
          </div>
        </div>
      </div>
      <div className="card">
        <h4>Source</h4>
        <div className="kv">
          <span>path</span>
          <span className="mono small" style={{ wordBreak: "break-all" }}>
            {flight.source_path}
          </span>
          <span>files</span>
          <span className="mono small">{flight.files.join(", ")}</span>
          <span>added</span>
          <span>{fmtDate(flight.created_at)}</span>
        </div>
      </div>
      <div className="card">
        <h4>Sync quality {quality.isFetching ? <Spinner /> : null}</h4>
        {quality.isError ? <span className="badge err">{errText(quality.error)}</span> : null}
        {q ? (
          <div className="kv">
            <span>frame</span>
            <span>{q.frame ?? "—"}</span>
            <span>duration</span>
            <span>
              {fmtDuration(q.total_seconds)} ({q.total_seconds}s)
            </span>
            <span>words / s</span>
            <span>{q.words_per_second ?? "—"}</span>
            <span>frames</span>
            <span>
              {q.valid_frames} / {q.frames} valid ({q.valid_pct}%)
            </span>
            <span>gaps</span>
            <span>{q.gaps} frame(s) filled with NaN</span>
            <span>sync segments</span>
            <span>
              {q.sync_segments}
              {q.reverse_sync ? " · reverse sync" : ""}
              {q.tsc_mode ? ` · ${q.tsc_mode}` : ""}
            </span>
            <span>parameters</span>
            <span>{q.parameters}</span>
          </div>
        ) : !projectId ? (
          <span className="dim">Select a project to decode this flight with its frame.</span>
        ) : null}
        {q?.segments && q.segments.length > 1 ? (
          <details style={{ marginTop: 6 }}>
            <summary className="dim small">segments</summary>
            <table className="tbl">
              <thead>
                <tr>
                  <th>start</th>
                  <th>frames</th>
                  <th>shift</th>
                </tr>
              </thead>
              <tbody>
                {q.segments.map((s, i) => (
                  <tr key={i}>
                    <td className="num">{s.start_frame}</td>
                    <td className="num">{s.frame_count}</td>
                    <td className="num">{s.shift}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : null}
      </div>
    </div>
  );
}

// -- server folder browser -----------------------------------------------------------------

function BrowseDialog({ onClose }: { onClose: () => void }) {
  const roots = useFlightRoots();
  const [root, setRoot] = useState(0);
  const [path, setPath] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [manual, setManual] = useState("");
  const browse = useBrowse(root, path, (roots.data?.length ?? 0) > 0);
  const register = useRegisterFlights();
  const toast = useSession((s) => s.toast);

  const crumbs = path ? path.split("/") : [];
  const flightsHere = browse.data?.entries.filter((e) => e.type === "flight") ?? [];

  const doRegister = () => {
    const bodies = [...picked].map((p) => ({ root: String(root), path: p }));
    if (manual.trim()) bodies.push({ path: manual.trim() } as { root: string; path: string });
    register.mutate(bodies, {
      onSuccess: (r) => {
        toast(`Registered ${r.length} flight(s)`, "success");
        onClose();
      },
      onError: (e) => toast(errText(e), "error"),
    });
  };

  return (
    <Modal
      title="Register flights from a server folder"
      onClose={onClose}
      width={720}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={(!picked.size && !manual.trim()) || register.isPending} onClick={doRegister}>
            Register {picked.size || ""}
          </button>
        </>
      }
    >
      {roots.data?.length ? (
        <>
          <div className="row">
            <select className="inp sm" value={root} onChange={(e) => (setRoot(Number(e.target.value)), setPath(""))}>
              {roots.data.map((r) => (
                <option key={r.index} value={r.index}>
                  {r.path}
                </option>
              ))}
            </select>
            <span className="mono small dim">
              /{" "}
              <a href="#" onClick={(e) => (e.preventDefault(), setPath(""))}>
                root
              </a>
              {crumbs.map((c, i) => (
                <span key={i}>
                  {" / "}
                  <a href="#" onClick={(e) => (e.preventDefault(), setPath(crumbs.slice(0, i + 1).join("/")))}>
                    {c}
                  </a>
                </span>
              ))}
            </span>
            <span className="spacer" />
            {flightsHere.length ? (
              <button
                className="btn sm"
                onClick={() => {
                  const next = new Set(picked);
                  const all = flightsHere.every((f) => next.has(f.path));
                  for (const f of flightsHere) all ? next.delete(f.path) : next.add(f.path);
                  setPicked(next);
                }}
              >
                {flightsHere.every((f) => picked.has(f.path)) ? "Unselect all" : `Select all ${flightsHere.length}`}
              </button>
            ) : null}
          </div>
          <div className="browse-list">
            {browse.isLoading ? <Spinner label="reading folder" /> : null}
            {browse.isError ? <span className="badge err">{errText(browse.error)}</span> : null}
            {browse.data?.entries.map((e) => (
              <div
                key={e.path}
                className={`list-item${picked.has(e.path) ? " sel" : ""}`}
                onClick={() => {
                  if (e.type === "folder") setPath(e.path);
                  else if (e.type === "flight") {
                    const next = new Set(picked);
                    next.has(e.path) ? next.delete(e.path) : next.add(e.path);
                    setPicked(next);
                  }
                }}
              >
                <span>{e.type === "folder" ? "📁" : e.type === "flight" ? "✈" : "📄"}</span>
                <span className="name mono">{e.name}</span>
                {e.type === "flight" ? (
                  <>
                    <span className="badge">{e.files} .tsc</span>
                    {e.tail ? <span className="badge info">{e.tail}</span> : null}
                    {e.date ? <span className="dim small">{fmtDate(e.date)}</span> : null}
                    <input type="checkbox" readOnly checked={picked.has(e.path)} />
                  </>
                ) : null}
              </div>
            ))}
            {browse.data && !browse.data.entries.length ? <span className="dim">empty folder</span> : null}
          </div>
        </>
      ) : (
        <p className="dim small">
          No flight roots configured. Set <code>REPORTS_STUDIO_FLIGHT_ROOTS='["D:/flights"]'</code> on the server, or enter an absolute path below.
        </p>
      )}
      <label className="field">
        <span>Or an absolute server path (a .tsc folder, .tsc file or FlightData .parquet)</span>
        <input className="inp" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="D:\flights\XA-HPY\68B6FEE8" />
      </label>
    </Modal>
  );
}

// -- upload ---------------------------------------------------------------------------------

function UploadDialog({ onClose }: { onClose: () => void }) {
  const upload = useUploadFlight();
  const toast = useSession((s) => s.toast);
  const [files, setFiles] = useState<File[]>([]);
  const [tail, setTail] = useState("");
  const [folderName, setFolderName] = useState("");
  const [over, setOver] = useState(false);

  const addFiles = useCallback((list: FileList | File[]) => {
    const arr = Array.from(list).filter((f) => f.name.toLowerCase().endsWith(".tsc"));
    setFiles((prev) => [...prev, ...arr]);
    const rel = (arr[0] as File & { webkitRelativePath?: string })?.webkitRelativePath;
    if (rel && rel.includes("/")) {
      const parts = rel.split("/");
      setFolderName((f) => f || parts[parts.length - 2] || "");
      if (parts.length >= 3) setTail((t) => t || parts[parts.length - 3]);
    }
  }, []);

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const items = Array.from(e.dataTransfer.items ?? []);
    const collected: File[] = [];
    const walk = async (entry: FileSystemEntry, folder: string): Promise<void> => {
      if (entry.isFile) {
        const f = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
        if (f.name.toLowerCase().endsWith(".tsc")) collected.push(f);
        if (folder && !folderName) setFolderName(folder);
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        const entries = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        for (const en of entries) await walk(en, entry.name);
      }
    };
    for (const it of items) {
      const entry = it.webkitGetAsEntry?.();
      if (entry) await walk(entry, "");
      else if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) collected.push(f);
      }
    }
    if (collected.length) addFiles(collected);
    else if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  return (
    <Modal
      title="Upload a flight"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!files.length || upload.isPending}
            onClick={() =>
              upload.mutate(
                { files, tail: tail || undefined, folderName: folderName || undefined },
                {
                  onSuccess: (r) => {
                    toast(`Uploaded ${r.files.length} file(s) as ${r.tail ?? r.id}`, "success");
                    onClose();
                  },
                  onError: (e) => toast(errText(e), "error"),
                },
              )
            }
          >
            {upload.isPending ? "Uploading…" : `Upload ${files.length || ""}`}
          </button>
        </>
      }
    >
      <div
        className={`dropzone${over ? " over" : ""}`}
        onDragOver={(e) => (e.preventDefault(), setOver(true))}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        Drop the flight's <code>.tsc</code> files or its folder here
        <div className="row" style={{ justifyContent: "center", marginTop: 8, gap: 12 }}>
          <label className="btn sm">
            Choose files
            <input type="file" multiple accept=".tsc" style={{ display: "none" }} onChange={(e) => e.target.files && addFiles(e.target.files)} />
          </label>
          <label className="btn sm">
            Choose folder
            <input
              type="file"
              style={{ display: "none" }}
              // @ts-expect-error non-standard attribute
              webkitdirectory=""
              directory=""
              onChange={(e) => e.target.files && addFiles(e.target.files)}
            />
          </label>
        </div>
      </div>
      {files.length ? (
        <div className="mono small dim">
          {files.map((f, i) => (
            <div key={i}>
              {f.name} <span className="faint">({Math.round(f.size / 1024)} KB)</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="row">
        <label className="field" style={{ flex: 1 }}>
          <span>Tail</span>
          <input className="inp sm" value={tail} onChange={(e) => setTail(e.target.value)} placeholder="XA-HPY" />
        </label>
        <label className="field" style={{ flex: 1 }}>
          <span>Folder name (hex download time, optional)</span>
          <input className="inp sm" value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="68B6FEE8" />
        </label>
      </div>
    </Modal>
  );
}
